/**
 * Query key factories for organizing React Query cache keys by feature.
 *
 * Following the pattern from TanStack Query documentation:
 * https://tanstack.com/query/latest/docs/framework/react/community/lukemorales-query-key-factory
 *
 * Benefits:
 * - Centralized key management
 * - Type-safe query keys
 * - Easy cache invalidation
 * - Consistent naming across features
 */

export const queryKeys = {
  /**
   * Tenant-related query keys
   */
  tenants: {
    all: ['tenants'] as const,
    lists: () => [...queryKeys.tenants.all, 'list'] as const,
    list: <TFilters extends object>(filters?: TFilters) =>
      [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
    overview: () => [...queryKeys.tenants.all, 'overview'] as const,
    overviewDetail: (type: string, month: string) => [...queryKeys.tenants.all, 'overview', 'detail', type, month] as const,
  },

  /**
   * License-related query keys
   */
  licenses: {
    all: ['licenses'] as const,
    lists: () => [...queryKeys.licenses.all, 'list'] as const,
    list: (tenantId: string) => [...queryKeys.licenses.lists(), tenantId] as const,
    details: () => [...queryKeys.licenses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.licenses.details(), id] as const,
    packages: (managementType?: string) => [...queryKeys.licenses.all, 'packages', managementType] as const,
  },
  /**
   * Account (UMS) query keys
   */
  accounts: {
    all: ['accounts'] as const,
    lists: () => [...queryKeys.accounts.all, 'list'] as const,
    list: <TFilters extends object>(filters?: TFilters) =>
      [...queryKeys.accounts.lists(), filters] as const,
    details: () => [...queryKeys.accounts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.accounts.details(), id] as const,
    statuses: () => [...queryKeys.accounts.all, 'statuses'] as const,
  },

  /**
   * Tenant settings query keys (SSO providers, Slack)
   */
  tenantSettings: {
    all: (tenantId: string) => ['tenantSettings', tenantId] as const,
    ssoProviders: (tenantId: string) => [...queryKeys.tenantSettings.all(tenantId), 'ssoProviders'] as const,
    slack: (tenantId: string) => [...queryKeys.tenantSettings.all(tenantId), 'slack'] as const,
  },

  /**
   * Seeding query keys (a tenant's demo data)
   */
  seedings: {
    all: ['seedings'] as const,
    demo: (tenantId: string) => [...queryKeys.seedings.all, 'demo', tenantId] as const,
  },

  /**
   * Feature toggle query keys
   */
  featureToggles: {
    all: ['featureToggles'] as const,
    lists: () => [...queryKeys.featureToggles.all, 'list'] as const,
    list: <TFilters extends object>(filters?: TFilters) =>
      [...queryKeys.featureToggles.lists(), filters] as const,
  },

} as const
